import React, { useEffect, useState } from 'react';
import { ActionButton, type ActionButtonData } from '../action-button/ActionButton';
import { CollapsibleSection } from '../collapsible-section/CollapsibleSection';
import { LabeledCheckbox, type LabeledCheckboxData } from '../labeled-checkbox/LabeledCheckbox';
import { t } from '../../services/locale';
import type {
  ProfileAccountBranch,
  ProfileAddress,
  ProfileIdentity,
  ProfileRestrictedField,
} from './profileAccount.types';
import {
  isRestrictedFieldDisabled,
  resolveProfileImageClick,
  shouldShowRestrictedDialogForField,
  showTaxIdOnProfile,
  userCanChangeImage,
  type ProfileImageGateContext,
} from './profileIdentityRules';

export const PROFILE_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
export const PROFILE_FIRST_NAME_PATTERN = /[\w\s'-]{3,30}/.source;
export const PROFILE_LAST_NAME_PATTERN = /[\w\s'-]{3,60}/.source;
export const PROFILE_PHONE_PATTERN = /^[+]?([0-9-]{8,12})$/.source;
export const PROFILE_ADDRESS_PATTERN = /[\w\s.,'#/-]{3,16}/.source;

export type ProfileEditFormData = {
  profile?: ProfileIdentity | null;
  branch?: ProfileAccountBranch | null;
  imageVersion?: number | string;
  imagePreviewUrl?: string | null;
  hasUploadedProfileImage?: boolean;
  updateProfileRestricted?: boolean;
  isInputDisabled?: boolean;
  isAddressExpanded?: boolean;
  mandatoryEmergencyContact?: boolean;
  canChangeImage?: boolean;
  errors?: string[];
  consentCheckboxes?: LabeledCheckboxData[];
  saveActionData?: ActionButtonData;
  onLogOut?: () => void;
  onToggleAddress?: () => void;
  onShowRestrictedDialog?: () => void;
  onShowFirstTimeImageDialog?: () => void;
  onProfilePatch?: (patch: Partial<ProfileIdentity>) => void;
  onAddressPatch?: (patch: Partial<ProfileAddress>) => void;
  onSelectImage?: (file: File | null) => void;
  onSave?: () => void;
};

type Props = Readonly<{
  data?: ProfileEditFormData;
}>;

type FieldProps = Readonly<{
  profile: ProfileIdentity;
  data: ProfileEditFormData;
}>;

type PreviewSetter = (url: string | null) => void;

const ADDRESS_INPUTS: ReadonlyArray<{ labelKey: string; name: string; field: keyof ProfileAddress }> = [
  { labelKey: 'WEB_STREET', name: 'modifyAddressStreet', field: 'street' },
  { labelKey: 'WEB_CITY', name: 'modifyAddressCity', field: 'city' },
  { labelKey: 'WEB_STATE', name: 'modifyAddressState', field: 'state' },
  { labelKey: 'WEB_COUNTRY', name: 'modifyAddressCountry', field: 'country' },
  { labelKey: 'WEB_POSTAL_CODE', name: 'modifyAddressPostalCode', field: 'postal_code' },
];

function decodeAmpersand(value: string): string {
  return value.replace(/&amp;/g, '&');
}

function pictureStyle(url: string | null | undefined, imageVersion?: number | string): React.CSSProperties | undefined {
  if (!url) {
    return undefined;
  }
  const version = imageVersion ?? '';
  return { backgroundImage: `url(${url}?ver=${version})` };
}

function LockIcon() {
  return (
    <svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 1a5 5 0 0 0-5 5v4H6a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V11a1 1 0 0 0-1-1h-1V6a5 5 0 0 0-5-5zm-3 5a3 3 0 1 1 6 0v4h-6V6zm3 8a1.5 1.5 0 0 1 1 2.618V19a1 1 0 1 1-2 0v-2.382A1.5 1.5 0 0 1 12 14z" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14.69 3.1l6.21 6.2-1.42 1.42-6.2-6.21 1.41-1.41zm-2.83 2.83l6.2 6.2L7.76 22H1v-6.76l10.86-10.3zm-8.4 13.53h2.83l8.3-8.3-2.83-2.83-8.3 8.3V19.46z" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M7.5 1v7h1V1z" />
      <path d="M3 8.812a5 5 0 0 1 2.578-4.375l-.485-.874A6 6 0 1 0 11 3.616l-.501.865A5 5 0 1 1 3 8.812" />
    </svg>
  );
}

function FieldLabel({
  label,
  showLock,
}: Readonly<{ label: string; showLock?: boolean }>) {
  return (
    <span className="input-label-with-icon">
      {label}
      {showLock ? (
        <div>
          <LockIcon />
        </div>
      ) : null}
    </span>
  );
}

function revokeObjectUrl(url: string | null) {
  if (!url) {
    return;
  }
  if (typeof URL === 'undefined') {
    return;
  }
  if (typeof URL.revokeObjectURL !== 'function') {
    return;
  }
  URL.revokeObjectURL(url);
}

function createObjectUrl(file: File): string | null {
  if (typeof URL === 'undefined') {
    return null;
  }
  if (typeof URL.createObjectURL !== 'function') {
    return null;
  }
  return URL.createObjectURL(file);
}

function useLocalImagePreview(): readonly [string | null, PreviewSetter] {
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      revokeObjectUrl(localPreview);
    };
  }, [localPreview]);

  return [localPreview, setLocalPreview];
}

function firstSelectedFile(input: HTMLInputElement): File | null {
  const files = input.files;
  if (!files) {
    return null;
  }
  if (files.length === 0) {
    return null;
  }
  return files[0];
}

function onProfileImageChange(
  event: React.ChangeEvent<HTMLInputElement>,
  localPreview: string | null,
  setLocalPreview: PreviewSetter,
  onSelectImage?: (file: File | null) => void,
) {
  const file = firstSelectedFile(event.target);
  event.target.value = '';
  if (!file) {
    return;
  }
  if (file.size > PROFILE_IMAGE_MAX_BYTES) {
    return;
  }
  revokeObjectUrl(localPreview);
  setLocalPreview(createObjectUrl(file));
  onSelectImage?.(file);
}

function clearProfileImage(
  localPreview: string | null,
  setLocalPreview: PreviewSetter,
  onSelectImage?: (file: File | null) => void,
) {
  revokeObjectUrl(localPreview);
  setLocalPreview(null);
  onSelectImage?.(null);
}

function onProfileImageClick(
  event: React.MouseEvent<HTMLInputElement>,
  restricted: boolean,
  hasUploadedProfileImage: boolean,
  data: ProfileEditFormData,
) {
  const outcome = resolveProfileImageClick({
    updateProfileRestricted: restricted,
    hasUploadedProfileImage,
  });
  if (outcome === 'show-restricted-dialog') {
    event.preventDefault();
    data.onShowRestrictedDialog?.();
    return;
  }
  if (outcome === 'show-first-time-dialog') {
    event.preventDefault();
    data.onShowFirstTimeImageDialog?.();
  }
}

function submitProfileEdit(event: React.FormEvent<HTMLFormElement>, onSave?: () => void) {
  event.preventDefault();
  const form = event.currentTarget;
  if (!form.checkValidity()) {
    form.reportValidity();
    return;
  }
  onSave?.();
}

function imageGate(data: ProfileEditFormData, restricted: boolean): ProfileImageGateContext {
  return {
    hasUser: true,
    hasBranch: true,
    updateProfileRestricted: restricted,
    hasUploadedProfileImage: Boolean(data.hasUploadedProfileImage),
  };
}

function canChangeProfileImage(data: ProfileEditFormData, restricted: boolean): boolean {
  if (typeof data.canChangeImage !== 'boolean') {
    return userCanChangeImage(imageGate(data, restricted));
  }
  return data.canChangeImage;
}

function displayValue(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value;
}

function addressFieldValue(address: ProfileAddress | null | undefined, field: keyof ProfileAddress): string {
  if (!address) {
    return '';
  }
  return displayValue(address[field]);
}

function patchAddress(data: ProfileEditFormData, field: keyof ProfileAddress, value: string) {
  data.onAddressPatch?.({ [field]: value });
}

function taxIdValue(profile: ProfileIdentity): string {
  const fiscal = profile.metadata?.fiscal;
  if (!fiscal) {
    return '';
  }
  return displayValue(fiscal.tax_id);
}

function taxIdPatch(profile: ProfileIdentity, taxId: string): Partial<ProfileIdentity> {
  const metadata = profile.metadata ?? {};
  const fiscal = metadata.fiscal ?? {};
  return {
    metadata: {
      ...metadata,
      fiscal: {
        ...fiscal,
        tax_id: taxId,
      },
    },
  };
}

function onRestrictedClick(
  field: ProfileRestrictedField,
  profile: ProfileIdentity,
  data: ProfileEditFormData,
) {
  const restricted = Boolean(data.updateProfileRestricted);
  if (shouldShowRestrictedDialogForField(field, profile, restricted)) {
    data.onShowRestrictedDialog?.();
  }
}

function fieldIsDisabled(field: ProfileRestrictedField, profile: ProfileIdentity, data: ProfileEditFormData): boolean {
  return isRestrictedFieldDisabled(field, profile, Boolean(data.isInputDisabled));
}

function LogoutControl({ onLogOut }: Readonly<{ onLogOut?: () => void }>) {
  return (
    <button
      type="button"
      className="log-out-svg"
      data-testid="logout-profile"
      aria-label={t('WEB_LOGOUT')}
      onClick={() => onLogOut?.()}
    >
      <LogoutIcon />
      <span className="log-out-text">{t('WEB_LOGOUT')}</span>
    </button>
  );
}

function ProfilePicture({
  imageUrl,
  imageVersion,
}: Readonly<{ imageUrl?: string | null; imageVersion?: number | string }>) {
  return (
    <div className="picture">
      <div className="picture-inner" style={pictureStyle(imageUrl, imageVersion)} />
    </div>
  );
}

function ImagePreview({ url }: Readonly<{ url: string }>) {
  return (
    <div className="picture-overlay-new">
      <img src={url} alt="" />
    </div>
  );
}

function ClearImageButton({ onClear }: Readonly<{ onClear: () => void }>) {
  return (
    <button type="button" className="picture-overlay-x" aria-label="Clear image" onClick={onClear}>
      <span className="icon-x" />
    </button>
  );
}

function PictureIcons({ canChangeImage }: Readonly<{ canChangeImage: boolean }>) {
  const lock = canChangeImage ? null : <LockIcon />;
  const edit = canChangeImage ? <EditIcon /> : null;
  return (
    <div className="picture-icon-bg">
      <div>{lock}</div>
      <div>
        <div />
      </div>
      <div>{edit}</div>
    </div>
  );
}

function ImageOverlay({
  data,
  previewUrl,
  localPreview,
  setLocalPreview,
}: Readonly<{
  data: ProfileEditFormData;
  previewUrl: string | null;
  localPreview: string | null;
  setLocalPreview: PreviewSetter;
}>) {
  const restricted = Boolean(data.updateProfileRestricted);
  const canChangeImage = canChangeProfileImage(data, restricted);
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onProfileImageChange(event, localPreview, setLocalPreview, data.onSelectImage);
  };
  const preview = previewUrl ? <ImagePreview url={previewUrl} /> : null;
  const clear = previewUrl ? (
    <ClearImageButton onClear={() => clearProfileImage(localPreview, setLocalPreview, data.onSelectImage)} />
  ) : null;

  return (
    <div className="picture-overlay">
      <input
        type="file"
        name="userImage"
        accept="image/*"
        aria-label="Profile image"
        onClick={(event) => onProfileImageClick(event, restricted, Boolean(data.hasUploadedProfileImage), data)}
        onChange={onChange}
      />
      <input
        type="file"
        id="profileImageInput"
        style={{ display: 'none' }}
        accept="image/*"
        onChange={onChange}
      />
      {preview}
      {clear}
      <PictureIcons canChangeImage={canChangeImage} />
    </div>
  );
}

function NameRow({ profile, data }: FieldProps) {
  const showLock = Boolean(data.updateProfileRestricted);
  return (
    <div className="text-input">
      <div className="text-input-half">
        <FieldLabel label={t('WEB_FIRST_NAME')} showLock={showLock} />
        <input
          pattern={PROFILE_FIRST_NAME_PATTERN}
          type="text"
          name="modifyFirstName"
          required
          value={displayValue(profile.first_name)}
          disabled={fieldIsDisabled('first_name', profile, data)}
          onClick={() => onRestrictedClick('first_name', profile, data)}
          onChange={(event) => data.onProfilePatch?.({ first_name: event.target.value })}
        />
      </div>
      <div className="text-input-half">
        <FieldLabel label={t('WEB_LAST_NAME')} showLock={showLock} />
        <input
          pattern={PROFILE_LAST_NAME_PATTERN}
          type="text"
          name="modifyLastName"
          required
          value={displayValue(profile.last_name)}
          disabled={fieldIsDisabled('last_name', profile, data)}
          onClick={() => onRestrictedClick('last_name', profile, data)}
          onChange={(event) => data.onProfilePatch?.({ last_name: event.target.value })}
        />
      </div>
    </div>
  );
}

function EmailRow({ profile, data }: FieldProps) {
  return (
    <div className="text-row">
      <FieldLabel label={t('WEB_EMAIL')} showLock={Boolean(data.updateProfileRestricted)} />
      <input
        type="email"
        autoCapitalize="off"
        autoComplete="off"
        name="modifyEmail"
        required
        value={displayValue(profile.email)}
        disabled={fieldIsDisabled('email', profile, data)}
        onClick={() => onRestrictedClick('email', profile, data)}
        onBlur={(event) => data.onProfilePatch?.({ email: event.target.value.toLowerCase() })}
        onChange={(event) => data.onProfilePatch?.({ email: event.target.value })}
      />
    </div>
  );
}

function PhoneRow({ profile, data }: FieldProps) {
  return (
    <div className="text-input top-space">
      <FieldLabel label={t('WEB_PHONE')} showLock={Boolean(data.updateProfileRestricted)} />
      <input
        type="tel"
        pattern={PROFILE_PHONE_PATTERN}
        name="modifyPhone"
        minLength={8}
        placeholder={t('WEB_EDIT_PHONE_PH')}
        required
        value={displayValue(profile.phone)}
        disabled={fieldIsDisabled('phone', profile, data)}
        onClick={() => onRestrictedClick('phone', profile, data)}
        onChange={(event) => data.onProfilePatch?.({ phone: event.target.value })}
      />
    </div>
  );
}

function AddressInput({
  label,
  name,
  value,
  onChange,
}: Readonly<{ label: string; name: string; value: string; onChange: (value: string) => void }>) {
  return (
    <div className="text-input">
      {label}
      <input
        pattern={PROFILE_ADDRESS_PATTERN}
        type="text"
        name={name}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function AddressFields({ profile, data }: FieldProps) {
  return (
    <CollapsibleSection
      data={{
        id: 'collapsibleAdressField',
        label: t('WEB_ADDRESS'),
        expanded: Boolean(data.isAddressExpanded),
        onToggle: () => data.onToggleAddress?.(),
      }}
    >
      {ADDRESS_INPUTS.map((field) => (
        <AddressInput
          key={field.name}
          label={t(field.labelKey)}
          name={field.name}
          value={addressFieldValue(profile.address, field.field)}
          onChange={(value) => patchAddress(data, field.field, value)}
        />
      ))}
    </CollapsibleSection>
  );
}

function EmergencyField({ profile, data }: FieldProps) {
  return (
    <div className="text-input">
      {decodeAmpersand(t('WEB_EMERGENCY_CONTACT'))}
      <input
        type="tel"
        placeholder={t('WEB_EDIT_EC_PH')}
        required={Boolean(data.mandatoryEmergencyContact)}
        name="modifyEmergencyContact"
        value={displayValue(profile.emergency_contact)}
        onChange={(event) => data.onProfilePatch?.({ emergency_contact: event.target.value })}
      />
    </div>
  );
}

function TaxIdField({
  profile,
  branch,
  onProfilePatch,
}: Readonly<{
  profile: ProfileIdentity;
  branch: ProfileEditFormData['branch'];
  onProfilePatch?: ProfileEditFormData['onProfilePatch'];
}>) {
  if (!showTaxIdOnProfile(branch)) {
    return null;
  }
  return (
    <div className="text-input">
      {t('WEB_MEMBER_TAX_ID_NUMBER')}
      <input
        type="text"
        name="modifyTaxId"
        value={taxIdValue(profile)}
        onChange={(event) => onProfilePatch?.(taxIdPatch(profile, event.target.value))}
      />
    </div>
  );
}

function ConsentList({ consents }: Readonly<{ consents: LabeledCheckboxData[] }>) {
  return (
    <div className="consent-checkbox-container">
      <span className="consent-header">{t('WEB_GDPR_CONSENT_TO_MARKETING_HEADING_WEB_PORTAL')}</span>
      {consents.map((checkbox) => (
        <div className="consent-checkbox" key={checkbox.checkboxId ?? checkbox.agreeLabelKey}>
          <LabeledCheckbox data={checkbox} />
        </div>
      ))}
    </div>
  );
}

function ErrorList({ errors }: Readonly<{ errors: string[] }>) {
  if (errors.length === 0) {
    return null;
  }
  return (
    <div className="text-error">
      {errors.map((error) => (
        <span key={error}>{error}</span>
      ))}
    </div>
  );
}

function SaveAction({ saveActionData }: Readonly<{ saveActionData?: ActionButtonData }>) {
  if (!saveActionData) {
    return null;
  }
  return (
    <span className="profile-action-button profile-edit-submit-host">
      <ActionButton data={saveActionData} />
    </span>
  );
}

function consentItems(data: ProfileEditFormData): LabeledCheckboxData[] {
  if (!data.consentCheckboxes) {
    return [];
  }
  return data.consentCheckboxes;
}

function errorItems(data: ProfileEditFormData): string[] {
  if (!data.errors) {
    return [];
  }
  return data.errors;
}

function activePreview(data: ProfileEditFormData, localPreview: string | null): string | null {
  if (data.imagePreviewUrl) {
    return data.imagePreviewUrl;
  }
  return localPreview;
}

function ProfileFields({ data }: Readonly<{ data: ProfileEditFormData }>) {
  const profile = data.profile;
  if (!profile) {
    return null;
  }
  return (
    <div className="text text-input-container">
      <NameRow profile={profile} data={data} />
      <EmailRow profile={profile} data={data} />
      <PhoneRow profile={profile} data={data} />
      <AddressFields profile={profile} data={data} />
      <EmergencyField profile={profile} data={data} />
      <TaxIdField profile={profile} branch={data.branch} onProfilePatch={data.onProfilePatch} />
      <ConsentList consents={consentItems(data)} />
      <ErrorList errors={errorItems(data)} />
    </div>
  );
}

export function ProfileEditForm({ data }: Props) {
  const [localPreview, setLocalPreview] = useLocalImagePreview();
  if (!data?.profile) {
    return null;
  }

  return (
    <form
      className="modal-body-left-editing"
      name="editProfileForm"
      onSubmit={(event) => submitProfileEdit(event, data.onSave)}
      noValidate={false}
    >
      <LogoutControl onLogOut={data.onLogOut} />
      <div className="profile-edit-photo">
        <ProfilePicture imageUrl={data.profile.image_url} imageVersion={data.imageVersion} />
        <ImageOverlay
          data={data}
          previewUrl={activePreview(data, localPreview)}
          localPreview={localPreview}
          setLocalPreview={setLocalPreview}
        />
      </div>
      <ProfileFields data={data} />
      <SaveAction saveActionData={data.saveActionData} />
    </form>
  );
}
